<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useAnalytics } from '@/shared/lib/use-analitics';

type TabValue = string | number | object;

type TabItem = {
  label: string;
  value: TabValue;
  tag?: string;
};

type TabModel = TabItem | TabValue | null | undefined;
type TabVariant = 'underline' | 'segmented';

const props = defineProps<{
  tabs: TabItem[];
  modelValue: TabModel;
  fullWidth?: boolean;
  dense?: boolean;
  variant?: TabVariant;
  router?: 'params' | 'query';
  routerKey?: string;
  analytics?: string | ((value: string) => string);
}>();

const emit = defineEmits<{ (event: 'update:modelValue', value: any): void }>();

const { t } = useI18n();
const route = useRoute();
const appRouter = useRouter();
const containerRef = ref<HTMLElement | null>(null);
const tabRefs = ref<HTMLElement[]>([]);
const layoutMode = ref<'measure' | 'equal' | 'compact'>('measure');
const tabWidth = ref(0);
const routeField = computed(() => props.routerKey ?? 'name');
const isSegmented = computed(() => (props.variant ?? 'underline') === 'segmented');

const isTabItem = (value: TabModel): value is TabItem =>
  typeof value === 'object' && value !== null && 'value' in value && 'label' in value;

const getTabKey = (value: TabValue, index: number) =>
  typeof value === 'string' || typeof value === 'number' ? value : index;

const normalizeTabValue = (value: TabModel): string | null => {
  if (isTabItem(value)) {
    if (typeof value.value === 'string' || typeof value.value === 'number') {
      return String(value.value);
    }
    return null;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return null;
};

const selectTab = (tab: TabItem, modelValue: TabModel) => {
  emit('update:modelValue', isTabItem(modelValue) ? tab : tab.value);

  if (!props.router) return;

  const value = typeof tab.value === 'string' || typeof tab.value === 'number' ? String(tab.value) : null;
  if (!value) return;

  const key = routeField.value;
  const isParams = props.router === 'params';
  const currentSource = isParams ? route.params : route.query;
  const currentField = currentSource[key];
  const currentValue = Array.isArray(currentField) ? (currentField[0] ?? null) : (currentField ?? null);

  if (String(currentValue ?? '') === value) return;

  appRouter.replace({
    [isParams ? 'params' : 'query']: {
      ...currentSource,
      [key]: value,
    },
  });
};

const isActiveTab = (tab: TabItem, modelValue: TabModel) => {
  if (isTabItem(modelValue)) {
    return tab === modelValue || tab.value === modelValue.value || tab.label === modelValue.label;
  }
  return tab.value === modelValue;
};

const setTabRef = (el: Element | { $el: Element } | null, index: number) => {
  if (!el) return;
  const element = el instanceof Element ? el : el.$el;
  tabRefs.value[index] = element as HTMLElement;
};

const updateLayout = async () => {
  if (isSegmented.value) return;
  const container = containerRef.value;
  if (!container) return;
  const tabs = tabRefs.value.filter(Boolean);
  if (!tabs.length) return;
  layoutMode.value = 'measure';
  await nextTick();
  const gap = Number.parseFloat(getComputedStyle(container).columnGap || '0');
  const totalWidth = tabs.reduce((sum, tab) => sum + tab.offsetWidth, 0) + gap * (tabs.length - 1);
  const maxWidth = Math.max(...tabs.map((tab) => tab.offsetWidth));
  const fits = maxWidth > 0 && totalWidth <= container.clientWidth;
  layoutMode.value = fits ? 'equal' : 'compact';
  if (fits) tabWidth.value = maxWidth;
};

useResizeObserver(containerRef, updateLayout);
watch(
  () => props.modelValue,
  (value) => {
    if (!props.analytics) return;
    const tabValue = normalizeTabValue(value);
    if (!tabValue) return;
    const eventName =
      typeof props.analytics === 'function' ? props.analytics(tabValue) : `${props.analytics}${tabValue}`;
    if (!eventName) return;
    useAnalytics(eventName);
  },
  { immediate: true }
);
watch(
  () => props.tabs,
  () => updateLayout(),
  { deep: true }
);
onMounted(updateLayout);
</script>

<template>
  <div
    ref="containerRef"
    class="ui-tabs"
    :class="[
      `ui-tabs--${props.variant ?? 'underline'}`,
      {
        'ui-tabs--compact': !isSegmented && (layoutMode === 'compact' || props.fullWidth),
        'ui-tabs--equal': !isSegmented && layoutMode === 'equal' && !props.fullWidth,
        'ui-tabs--dense': props.dense,
        'ui-tabs--full-width': isSegmented && props.fullWidth,
      },
    ]"
    :style="!isSegmented && layoutMode === 'equal' && !props.fullWidth ? { '--tab-width': `${tabWidth}px` } : undefined"
  >
    <button
      v-for="(tab, index) in tabs"
      :key="getTabKey(tab.value, index)"
      :ref="(el) => setTabRef(el, index)"
      class="ui-tabs__tab"
      :class="{ 'is-active': isActiveTab(tab, modelValue) }"
      type="button"
      @click="selectTab(tab, modelValue)"
    >
      <span class="ui-tabs__label">{{ t(tab.label) }}</span>
      <span v-if="tab.tag" class="ui-tabs__tag">{{ tab.tag }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
@import '@/app/styles/quasar-variables';

.ui-tabs {
  display: inline-flex;
  align-items: flex-end;
  position: relative;
  gap: 32px;
  max-width: 100%;

  &__tab {
    border: none;
    background: transparent;
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-500;
    cursor: pointer;
    padding: 0 0 16px 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 0 0 auto;
    white-space: nowrap;
    transition: color 0.2s ease;
    border-bottom: 2px solid transparent;

    &.is-active {
      color: $primary;
      border-bottom-color: $blue-dark;
    }
  }

  &__tag {
    padding: 2px 6px;
    font-size: 0.625rem;
    line-height: 12px;
    color: $white;
    background: $success;
    border-radius: 10px;
    text-transform: uppercase;
  }

  &--compact {
    width: 100%;
    gap: 0;

    .ui-tabs__tab {
      flex: 1 1 50%;
      text-align: center;
    }
  }

  &--equal {
    gap: 32px;

    .ui-tabs__tab {
      flex: 0 0 var(--tab-width);
      width: var(--tab-width);
      text-align: center;
    }
  }

  &--segmented {
    align-items: center;
    gap: 0;
    min-height: 40px;
    padding: 2px;
    background: $gray-400;
    border-radius: 999px;
    overflow: hidden;

    .ui-tabs__tab {
      min-height: 36px;
      padding: 0 16px;
      border-bottom: none;
      border-radius: 999px;
      color: $primary;
      font-size: 0.875rem;
      line-height: 20px;
      font-weight: 600;
      transition:
        background 0.2s ease,
        box-shadow 0.2s ease;

      &.is-active {
        background: $white;
        border-bottom-color: transparent;
        box-shadow: 0 2px 6px rgba($black, 0.12);
      }
    }
  }

  &--segmented.ui-tabs--full-width {
    width: 100%;

    .ui-tabs__tab {
      flex: 1 1 50%;
    }
  }

  &--segmented.ui-tabs--dense {
    min-height: 28px;

    .ui-tabs__tab {
      min-height: 28px;
      padding: 0 12px;
      font-size: 0.875rem;
      line-height: 16px;
    }
  }
}
</style>
